/**
 * HAVVA analysis helpers
 *
 * One tolerant normaliser for every HAVVA analysis shape the dashboard meets:
 *  - the raw middleware response of POST /upload-image and GET /detail-analysis
 *    (healthy_percentage, image, timeStamp, ...)
 *  - history entries kept in React state / localStorage 'havva_upload_history'
 *    (healthyPercentage, annotatedImage, ...)
 *  - rows of the Supabase table havva_analysis_history
 *    (analysis_id, camera_id, annotated_image_url, analyzed_at, analysis_json, ...)
 *
 * HAVVA has answered /upload-image in three ways (all real, see the fixtures
 * used by the normaliser test):
 *  - OLD format (Apr-Jul 2026): healthy_percentage / unhealthy_percentage as
 *    numbers, recommendedActivity as text, image as a plain URL.
 *  - NEW format (Sep 2026): the old fields are the STRING "N/A" and the result
 *    is in plant_identification, health_assessment, growth_assessment,
 *    health_explanation, growth_explanation, recommended_checks and summary;
 *    image is a signed URL that expires 24 hours after the job.
 *  - all "N/A" (Oct 2026): HTTP 200, a real id and nothing else.
 * Nothing in the UI may trust a field to be a number, a date, a URL or even
 * present without going through here, and no component reads the vendor's
 * field names: the new-format fields come out as crop, health, growth,
 * summary, healthExplanation, growthExplanation and recommendedChecks.
 */

// New-format fields under HAVVA's own names (job API), for reference. The
// normalised object carries them under the names listed above.
export const HAVVA_NEW_FORMAT_FIELDS = [
  'plant_identification',
  'health_assessment',
  'growth_assessment',
  'health_explanation',
  'growth_explanation',
  'recommended_checks',
  'summary'
];

// History bookkeeping that is not analysis content; carried through untouched.
const HISTORY_META_FIELDS = ['dbId', 'farmId', 'fileName', 'savedAt', 'userId'];

// Version of the object written to havva_analysis_history.analysis_json
export const HAVVA_ANALYSIS_JSON_VERSION = 1;

// Shown under every new-format result
export const HAVVA_SCREENING_NOTE = 'AI-assisted screening only. Not a laboratory diagnosis.';

export const HAVVA_EMPTY_VALUE = '—';

// Placeholders that stand for "no value". The middleware sends "N/A" today;
// the near-variants are accepted so a small change on the vendor side
// ("NA", "N.A.", "null", "None", "-") is not mistaken for a real analysis.
const MISSING_TEXT = /^(n\/?a|n\.a\.?|null|none|nil|undefined|nan|[-\u2013\u2014]+)$/i;

/**
 * "N/A", "n/a", "", null and undefined all mean "the vendor sent nothing".
 */
export const isMissing = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' || MISSING_TEXT.test(trimmed);
  }
  if (typeof value === 'number') return Number.isNaN(value);
  return false;
};

// First candidate that is not missing, else null
const pick = (...candidates) => {
  for (const candidate of candidates) {
    if (!isMissing(candidate)) return candidate;
  }
  return null;
};

const toText = (value) => {
  if (isMissing(value)) return null;
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
};

// A file name is shown as it is ("none.jpg" stays), but it must be renderable:
// a string or number, never an object.
const toFileName = (value) => {
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
};

// Every piece of real text inside a value (string, array or object), joined.
const flattenText = (value) => {
  if (isMissing(value)) return null;
  if (typeof value === 'string') return value.trim();
  if (typeof value !== 'object') return null;
  const parts = (Array.isArray(value) ? value : Object.values(value)).map(flattenText).filter(Boolean);
  return parts.length > 0 ? parts.join('; ') : null;
};

/**
 * Text that is safe to render: a non-missing string/number as a string, else
 * null. (Rendering an object as a React child throws.)
 */
export const toDisplayText = toText;

// First candidate that is a non-missing string or number (kept in its own
// type), else null. Objects never get through, so the result is renderable.
const pickScalar = (...candidates) => {
  for (const candidate of candidates) {
    if (isMissing(candidate)) continue;
    if (typeof candidate === 'number') return candidate;
    if (typeof candidate === 'string') return candidate.trim();
  }
  return null;
};

/**
 * Percentages arrive as numbers, numeric strings ("82.5", "82.5%") or "N/A".
 * Anything that does not parse to a finite number is null.
 */
export const parsePercentage = (value) => {
  if (isMissing(value)) return null;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const isHttpUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value.trim());

export const isImageDataUri = (value) => typeof value === 'string' && /^data:image\//i.test(value.trim());

// Only an http(s) URL or a data:image URI is an image; "N/A" and friends are not
const toImage = (value) => (isHttpUrl(value) || isImageDataUri(value) ? value.trim() : null);

// Query parameters that mark a URL as signed, i.e. valid for a limited time
const SIGNED_URL_PARAM = /^(expires?|expiry|signature|sig|token|x-amz-[\w-]+|x-goog-[\w-]+)$/i;

/**
 * True for a signed (expiring) URL, such as HAVVA's annotated image
 * ".../annotated-image?expires=1788357280&signature=...", which stops working
 * 24 hours after the job. Such a URL must never be stored as the long-term
 * image of a record.
 */
export const isSignedUrl = (value) => {
  if (!isHttpUrl(value)) return false;
  const queryStart = value.indexOf('?');
  if (queryStart === -1) return false;
  const query = value.slice(queryStart + 1).split('#')[0];
  return query.split('&').some((pair) => SIGNED_URL_PARAM.test(pair.split('=')[0]));
};

// "2026-07-15T10:00:00" / "2026-07-15 10:00:00" - a date-time with no zone
const ZONELESS_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

const toIsoTimestamp = (value) => {
  if (isMissing(value)) return null;
  if (typeof value !== 'string' && typeof value !== 'number' && !(value instanceof Date)) return null;
  let input = value;
  if (typeof value === 'string') {
    // A date-time without a zone is read as UTC. That is how Postgres read
    // the raw string when it was stored unparsed (before Oct 2026), so new
    // rows line up with the old ones instead of shifting by the browser's
    // UTC offset. HAVVA has not confirmed which zone it means.
    const trimmed = value.trim();
    input = ZONELESS_DATETIME.test(trimmed) ? `${trimmed.replace(' ', 'T')}Z` : trimmed;
  }
  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const asObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

// Real text only: a non-missing string. A bare number or boolean
// (summary: 0) is not an explanation.
const toProse = (value) => (typeof value === 'string' && !isMissing(value) ? value.trim() : null);

// Keys an off-spec response may use for the label of an assessment
// ({ name: "kailan" }, { status: "unhealthy" }); "label" is HAVVA's own.
const LABEL_KEYS = ['label', 'name', 'status', 'class', 'value', 'result'];

const toLabel = (source) => {
  for (const key of LABEL_KEYS) {
    const text = toProse(source[key]);
    if (text !== null) return text;
  }
  return null;
};

// An assessment as an object: HAVVA sends { label, ... }; a bare string or a
// list of strings (["Canopy detected"]) is taken as the label.
const toAssessmentSource = (value) => (
  typeof value === 'string' || Array.isArray(value) ? { label: flattenText(value) } : asObject(value)
);

const toFiniteNumber = (value) => {
  if (isMissing(value)) return null;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Largest value still read as the fraction 1 (see toConfidence)
const CONFIDENCE_OVERSHOOT = 1.01;

/**
 * The AI's confidence as a fraction 0-1. HAVVA sends 0.9862; a percentage
 * ("98.6%", 98.6) is accepted too. Anything else is null.
 */
const toConfidence = (value) => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (isMissing(value)) return null;
  const isPercentText = typeof value === 'string' && value.trim().endsWith('%');
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  if (!isPercentText && parsed > 1) {
    // A fraction that overshoots by rounding (1.0000001) is 1, not "1%".
    // Between that and 1.5 it could be either a fraction or a percentage:
    // left out rather than guessed.
    if (parsed <= CONFIDENCE_OVERSHOOT) return 1;
    if (parsed <= 1.5) return null;
  }
  const fraction = isPercentText || parsed > 1 ? parsed / 100 : parsed;
  return fraction <= 1 ? fraction : null;
};

// { label, confidence } of plant_identification / health_assessment. A bare
// string ("kailan") is taken as the label. An object without a label under
// any known key gives whatever real text it holds, so an off-spec answer is
// still shown and saved. Without any text there is nothing to show
// ({ label: "N/A", confidence: 0 } is "no assessment").
const toAssessment = (value) => {
  const source = toAssessmentSource(value);
  const label = toLabel(source) ?? flattenText(source);
  if (label === null) return null;
  return { label, confidence: toConfidence(source.confidence) };
};

// health_assessment, with the label read as a status for the green/red badge.
// A label other than healthy / unhealthy keeps its text and gets status null.
const toHealth = (value) => {
  const assessment = toAssessment(value);
  if (!assessment) return null;
  const key = assessment.label.toLowerCase().replace(/[^a-z]/g, '');
  const status = key === 'healthy' ? 'healthy' : key === 'unhealthy' ? 'unhealthy' : null;
  return { label: assessment.label, status, confidence: assessment.confidence };
};

// growth_assessment: { label, confidence, note, metrics: { region_count,
// coverage_proxy } }. Needs a label or a note to count as present; an
// off-spec object gives whatever real text it holds as the label.
const toGrowth = (value) => {
  const source = toAssessmentSource(value);
  const metrics = asObject(source.metrics);
  const note = flattenText(source.note);
  const label = toLabel(source) ?? (note === null ? flattenText(source) : null);
  if (label === null && note === null) return null;
  return {
    label,
    confidence: toConfidence(source.confidence),
    note,
    regionCount: toFiniteNumber(pick(source.regionCount, metrics.region_count)),
    coverageProxy: toFiniteNumber(pick(source.coverageProxy, metrics.coverage_proxy))
  };
};

// recommended_checks: an array of strings. A single string is one check;
// entries without text are dropped. Always an array.
const toChecks = (value) => {
  if (isMissing(value)) return [];
  const list = Array.isArray(value) ? value : [value];
  return list.map(flattenText).filter(Boolean);
};

// havva_analysis_history.analysis_json: an object, or its JSON text
const parseStoredAnalysis = (value) => {
  if (typeof value !== 'string') return asObject(value);
  try {
    return asObject(JSON.parse(value));
  } catch (e) {
    return {};
  }
};

// Longest text stored in the recommended-activity slot
const RECOMMENDATION_MAX_LENGTH = 2000;

// The text that stands in for recommendedActivity when a result has only
// new-format fields: its recommended checks, else its summary.
const deriveRecommendationText = (analysis) => {
  const text = flattenText(analysis.recommendedChecks) || toProse(analysis.summary);
  return text ? text.slice(0, RECOMMENDATION_MAX_LENGTH).trim() : null;
};

/**
 * Normalise one HAVVA analysis (any of the shapes listed at the top).
 * Idempotent: normalising an already-normalised object gives the same object.
 *
 * New-format fields (null / [] when HAVVA did not send them):
 *   crop               { label, confidence }            plant_identification
 *   health             { label, status, confidence }    health_assessment
 *   growth             { label, confidence, note,       growth_assessment
 *                        regionCount, coverageProxy }
 *   summary, healthExplanation, growthExplanation       text
 *   recommendedChecks  [text, ...]                      recommended_checks
 * confidence is the AI's confidence as a fraction 0-1 - NOT a share of
 * healthy plants. The new format has no healthy / unhealthy percentages.
 * A history row's analysis_json (written by toAnalysisJson) is read first.
 *
 * isNewFormat    - at least one new-format field exists.
 * hasPercentages - a real healthy or unhealthy percentage exists (old format).
 * hasAnalysis    - at least one real analysis value exists: a finite
 *                  percentage, a non-empty recommendedActivity, or any
 *                  new-format field.
 * hasDetail      - hasAnalysis, or any legacy metric / annotated image (used by
 *                  the Detail Analysis tab, where a metrics-only record is valid).
 */
export const normalizeHavvaAnalysis = (raw) => {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};

  const healthyPercentage = parsePercentage(pick(source.healthyPercentage, source.healthy_percentage));
  const unhealthyPercentage = parsePercentage(pick(source.unhealthyPercentage, source.unhealthy_percentage));
  let recommendedActivity = pick(toText(source.recommendedActivity), toText(source.recommended_activity));
  const annotatedImage = pick(
    toImage(source.annotatedImage),
    toImage(source.image),
    toImage(source.annotated_image_url),
    toImage(source.annotatedImageUrl)
  );

  const id = pickScalar(source.id, source.analysis_id);
  // The middleware misspells "growtRateReduction"; accept every spelling
  const growthRateReduction = pickScalar(source.growthRateReduction, source.growtRateReduction, source.growth_rate_reduction);

  const normalized = {
    id: id === null ? null : String(id),
    cameraId: pickScalar(source.cameraId, source.camera_id),
    timeStamp: toIsoTimestamp(pick(source.timeStamp, source.analyzed_at, source.timestamp)),
    healthyPercentage,
    unhealthyPercentage,
    recommendedActivity,
    annotatedImage,
    // Legacy text fields
    leafDamage: pickScalar(source.leafDamage, source.leaf_damage),
    diseaseSpreadRate: pickScalar(source.diseaseSpreadRate, source.disease_spread_rate),
    plantHeightVariation: pickScalar(source.plantHeightVariation, source.plant_height_variation),
    leafColorDeviation: pickScalar(source.leafColorDeviation, source.leaf_color_deviation),
    growthRateReduction
  };

  // New-format fields. Read from the stored analysis_json first (history
  // rows), then from the object itself; under our names (an already
  // normalised object) or HAVVA's (a raw response).
  const stored = parseStoredAnalysis(pick(source.analysis_json, source.analysisJson));
  const field = (name, vendorName) => pick(stored[name], stored[vendorName], source[name], source[vendorName]);

  normalized.crop = toAssessment(field('crop', 'plant_identification'));
  normalized.health = toHealth(field('health', 'health_assessment'));
  normalized.growth = toGrowth(field('growth', 'growth_assessment'));
  // Text; an off-spec { text: "..." } or ["a", "b"] gives the text it holds
  normalized.summary = flattenText(pick(stored.summary, source.summary));
  normalized.healthExplanation = flattenText(field('healthExplanation', 'health_explanation'));
  normalized.growthExplanation = flattenText(field('growthExplanation', 'growth_explanation'));
  normalized.recommendedChecks = toChecks(field('recommendedChecks', 'recommended_checks'));

  normalized.isNewFormat =
    normalized.crop !== null ||
    normalized.health !== null ||
    normalized.growth !== null ||
    normalized.summary !== null ||
    normalized.healthExplanation !== null ||
    normalized.growthExplanation !== null ||
    normalized.recommendedChecks.length > 0;

  // A saved new-format record carries its checks / summary in the
  // recommended-activity column as a fallback (see getRecommendationText).
  // That copy is not something HAVVA recommended in the old sense, so it is
  // not reported as recommendedActivity next to the fields it was made from.
  if (recommendedActivity !== null && recommendedActivity === deriveRecommendationText(normalized)) {
    recommendedActivity = null;
    normalized.recommendedActivity = null;
  }

  HISTORY_META_FIELDS.forEach((metaField) => {
    if (source[metaField] === undefined) return;
    // fileName is rendered, so it must never be an object
    normalized[metaField] = metaField === 'fileName' ? toFileName(source[metaField]) : source[metaField];
  });

  normalized.hasPercentages = healthyPercentage !== null || unhealthyPercentage !== null;

  normalized.hasAnalysis =
    normalized.hasPercentages ||
    recommendedActivity !== null ||
    normalized.isNewFormat;

  normalized.hasDetail =
    normalized.hasAnalysis ||
    annotatedImage !== null ||
    normalized.leafDamage !== null ||
    normalized.diseaseSpreadRate !== null ||
    normalized.plantHeightVariation !== null ||
    normalized.leafColorDeviation !== null ||
    normalized.growthRateReduction !== null;

  return normalized;
};

/**
 * Text for the "recommended activity" slot of a saved record: the legacy
 * recommendedActivity when HAVVA sent one, otherwise the new-format
 * recommended checks, then the summary. Without this a result that carries
 * only new-format fields would be saved as an empty record wherever the
 * analysis_json column is not available.
 * Accepts a raw response or a normalised analysis.
 */
export const getRecommendationText = (analysis) => {
  if (!analysis || typeof analysis !== 'object') return null;
  const normalized = normalizeHavvaAnalysis(analysis);
  const own = normalized.recommendedActivity;
  return own ? own.slice(0, RECOMMENDATION_MAX_LENGTH) : deriveRecommendationText(normalized);
};

/**
 * Normalise the answer of GET /detail-analysis. Live / V1 answers are flat;
 * the V2 document nests them as { data: { metrics: {...}, annotatedImageUrl },
 * timestamp }. Both are flattened and read by normalizeHavvaAnalysis.
 */
export const normalizeHavvaDetail = (raw) => {
  const source = asObject(raw);
  const nested = asObject(source.data);
  const metrics = asObject(nested.metrics);
  return normalizeHavvaAnalysis({ ...source, ...nested, ...metrics });
};

// Not part of the stored analysis: the image has its own column (and may be a
// signed URL), the flags are derived, the rest is history bookkeeping.
const ANALYSIS_JSON_EXCLUDED = ['annotatedImage', 'isNewFormat', 'hasPercentages', 'hasAnalysis', 'hasDetail', ...HISTORY_META_FIELDS];

/**
 * The object stored in havva_analysis_history.analysis_json: the whole
 * normalised analysis of a new-format result (crop, health, growth, texts,
 * checks, plus the legacy fields) without the image and the bookkeeping.
 * null for a result with no new-format field - its columns already hold
 * everything. normalizeHavvaAnalysis reads it back from a history row.
 */
export const toAnalysisJson = (analysis) => {
  const normalized = normalizeHavvaAnalysis(analysis);
  if (!normalized.isNewFormat) return null;
  const json = { version: HAVVA_ANALYSIS_JSON_VERSION };
  Object.keys(normalized).forEach((key) => {
    if (!ANALYSIS_JSON_EXCLUDED.includes(key)) json[key] = normalized[key];
  });
  return json;
};

/**
 * Normalise a list of history entries. Anything that is not an object is
 * dropped. With dropEmpty, entries without any analysis are dropped as well
 * (used for localStorage, which collected "N/A" uploads before the fix).
 */
export const normalizeHavvaHistory = (list, { dropEmpty = false } = {}) => {
  if (!Array.isArray(list)) return [];
  return list
    .filter((entry) => entry && typeof entry === 'object' && !Array.isArray(entry))
    .map(normalizeHavvaAnalysis)
    .filter((entry) => !dropEmpty || entry.hasAnalysis);
};

/**
 * "82.5%" for a finite number, "—" for everything else. Never throws.
 */
export const formatPercentage = (value, digits = 1) => {
  const parsed = parsePercentage(value);
  return parsed === null ? HAVVA_EMPTY_VALUE : `${parsed.toFixed(digits)}%`;
};

/**
 * A confidence (fraction 0-1) as a whole percentage: 0.9862 -> "99%".
 * null when there is none, so the caller can leave it out.
 */
export const formatConfidence = (value) => {
  const confidence = toConfidence(value);
  return confidence === null ? null : `${Math.round(confidence * 100)}%`;
};

/**
 * A HAVVA label for display, first letter in upper case: "kailan" -> "Kailan".
 * null when there is no label.
 */
export const formatHavvaLabel = (value) => {
  const text = toProse(value);
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : null;
};

/**
 * Width for a progress bar: 0-100, with null / NaN / "N/A" as 0.
 */
export const clampPercentage = (value) => {
  const parsed = parsePercentage(value);
  if (parsed === null) return 0;
  return Math.min(100, Math.max(0, parsed));
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Date for a timestamp, day first: "16 Jul 2026", with the time
 * "16 Jul 2026, 4:12:35 PM" (the browser's time zone). The same in every
 * browser language, so it cannot be misread as month first. "—" when the
 * value does not parse.
 * @param {*} value - ISO string, epoch ms or Date
 * @param {boolean} withTime - include the time of day
 */
export const formatHavvaDate = (value, withTime = false) => {
  const iso = toIsoTimestamp(value);
  if (!iso) return HAVVA_EMPTY_VALUE;
  const date = new Date(iso);
  const day = `${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
  if (!withTime) return day;
  const twoDigits = (number) => String(number).padStart(2, '0');
  const hours = date.getHours();
  return `${day}, ${hours % 12 || 12}:${twoDigits(date.getMinutes())}:${twoDigits(date.getSeconds())} ${hours < 12 ? 'AM' : 'PM'}`;
};
